import React, { useState, useEffect } from 'react';
import { PublicIP, publicIpApi, Office } from '../services/api';
import ConfirmDialog from './ConfirmDialog';
import { useAuth } from '../contexts/AuthContext';

interface PublicIPListProps {
  selectedOffice: Office | null;
  onSelectPublicIP: (publicIP: PublicIP) => void;
}

const PublicIPList: React.FC<PublicIPListProps> = ({ selectedOffice, onSelectPublicIP }) => {
  const { canEdit } = useAuth();
  const [publicIPs, setPublicIPs] = useState<PublicIP[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [deleteConfirm, setDeleteConfirm] = useState<{ isOpen: boolean; publicIP: PublicIP | null }>({
    isOpen: false,
    publicIP: null
  });

  useEffect(() => {
    if (selectedOffice) {
      loadPublicIPs();
    } else {
      setPublicIPs([]);
    }
  }, [selectedOffice]);

  const loadPublicIPs = async () => {
    if (!selectedOffice) return;
    
    setLoading(true);
    setError('');
    try {
      const response = await publicIpApi.getAll(selectedOffice.id);
      setPublicIPs(response.data);
    } catch (err) {
      setError('Failed to load public IPs');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = (publicIP: PublicIP) => {
    setDeleteConfirm({ isOpen: true, publicIP });
  };

  const confirmDelete = async () => {
    if (!deleteConfirm.publicIP) return;
    
    try {
      await publicIpApi.delete(deleteConfirm.publicIP.id);
      await loadPublicIPs();
      setDeleteConfirm({ isOpen: false, publicIP: null });
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to delete public IP');
      console.error(err);
      setDeleteConfirm({ isOpen: false, publicIP: null });
    }
  };

  if (!selectedOffice) {
    return (
      <div className="card">
        <h2>Public IPs</h2>
        <p>Select an office to view public IPs</p>
      </div>
    );
  }

  return (
    <div className="card">
      <h2>Public IPs - {selectedOffice.building} {selectedOffice.floor}</h2>
      {loading && <p>Loading...</p>}
      {error && <div className="error">{error}</div>}
      <table>
        <thead>
          <tr>
            <th>CIDR</th>
            <th>ISP</th>
            <th>Description</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          {publicIPs.map((publicIP) => (
            <tr key={publicIP.id}>
              <td>{publicIP.cidr}</td>
              <td>{publicIP.isp}</td>
              <td>{publicIP.description}</td>
              <td>
                {canEdit() ? (
                  <>
                    <button className="btn btn-sm" onClick={() => onSelectPublicIP(publicIP)}>Edit</button>
                    <button className="btn btn-sm btn-danger" onClick={() => handleDelete(publicIP)}>Delete</button>
                  </>
                ) : (
                  <span>-</span>
                )}
              </td>
            </tr>
          ))}
          {publicIPs.length === 0 && !loading && (
            <tr>
              <td colSpan={4}>No public IPs found</td>
            </tr>
          )}
        </tbody>
      </table>

      <ConfirmDialog
        isOpen={deleteConfirm.isOpen}
        title="Delete Public IP"
        message={`Are you sure you want to delete public IP "${deleteConfirm.publicIP?.cidr}"?\n\nISP: ${deleteConfirm.publicIP?.isp || 'N/A'}\nDescription: ${deleteConfirm.publicIP?.description || 'N/A'}`}
        confirmText="Delete"
        cancelText="Cancel"
        onConfirm={confirmDelete}
        onCancel={() => setDeleteConfirm({ isOpen: false, publicIP: null })}
        variant="danger"
      />
    </div>
  );
};

export default PublicIPList;